import { styled } from '@mui/material/styles';
import { Dialog, Box, Typography } from '@mui/material';

// ========================================
// Main Dialog Container - Slide-in panel from right
// ========================================
export const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'fixed',
    right: 0,
    top: '100px',
    bottom: 0,
    width: '940px',
    height: 'auto',
    margin: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 0,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
});

// ========================================
// Fixed Header Wrapper
// ========================================
export const FixedHeaderWrapper = styled(Box)({
  position: 'sticky',
  top: 0,
  background: '#ffffff',
  zIndex: 10,
  padding: '20px 32px 0',
  flexShrink: 0,
});

// ========================================
// Scrollable Content Wrapper
// ========================================
export const ContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '10px 32px 20px',

  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
});

// ========================================
// Header Section
// ========================================
export const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '16px',
  flexShrink: 0,
});

export const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const TitleTypography = styled(Typography)({
  fontFamily: "'Lufga', sans-serif",
  fontWeight: 500,
  fontSize: '32px',
  lineHeight: '100%',
  color: '#0F52BA',
});

export const CloseIcon = styled(Box)({
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    color: 'var(--error)',
  },
}) as typeof Box;

export const ActionMenuButton = styled(Box)({
  width: '24px',
  height: '24px',
  minWidth: '24px',
  fontSize: '24px',
  color: '#75838A',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  background: 'transparent',
  border: 'none',
  '&:hover': {
    color: '#0B2330',
  },
}) as typeof Box;

// ========================================
// Main Content Grid (Left + Right Columns)
// ========================================
export const MainContentGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 325px',
  gap: '16px',
  alignItems: 'start',
});

export const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const RightColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// ========================================
// Info Row (Payee/Date, Voucher Type etc.)
// ========================================
export const InfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  paddingBottom: '16px',
  flexShrink: 0,
});

export const InfoBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const InfoBlockRight = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '8px',
});

export const LabelTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#75838A',
});

export const ValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '140%',
  color: '#0B2330',
});

// ========================================
// Bill Details Box
// ========================================
export const BillDetailsBox = styled(Box)({
  width: '100%',
  background: '#FAFAFA',
  borderRadius: '8px',
  padding: '12px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const BillRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const BillFieldBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
});

export const BillFieldBoxRight = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '8px',
});

export const AmountValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const TotalPaidTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const TotalBalanceLabel = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const BalanceLabel = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#F0A34A',
});

export const BalanceValue = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#0B2330',
});

// ========================================
// Check Payments Button
// ========================================
export const CheckPaymentsButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px 8px 8px',
  gap: '6px',
  background: '#F1F7FF',
  borderRadius: '8px',
  cursor: 'pointer',
  alignSelf: 'flex-end',
  marginTop: '8px',
  '&:hover': {
    background: '#E3EFFF',
  },
});

export const CheckPaymentsIcon = styled(Box)({
  width: '12px',
  height: '12px',
  color: '#0F52BA',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}) as typeof Box;

export const CheckPaymentsText = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '100%',
  color: '#0F52BA',
});

// ========================================
// Description Box (with expandable text)
// ========================================
export const DescriptionBox = styled(Box)({
  width: '100%',
  background: '#FAFAFA',
  borderRadius: '8px',
  padding: '12px',
  boxSizing: 'border-box',
});

export const DescriptionNoteContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

// ========================================
// Approve/Reject Status Box (Right Column)
// ========================================
export const StatusInfoBox = styled(Box)({
  border: '1px solid #DFDFDF',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const StatusInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const RejectionNoteBox = styled(Box)({
  background: 'rgba(232, 94, 81, 0.08)',
  borderRadius: '4px',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const RejectionLabelTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#E85E51',
});

export const SmallValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '100%',
  color: '#0B2330',
});
